import { useState } from "react";
import {
  getCommitment,
  setCommitment,
  completeCommitment,
  skipCommitment,
  commitmentStatus,
  formatTime,
} from "../utils/commitment";
import { prescriptionFor } from "../utils/mood";
import { hapticLight, hapticSuccess } from "../utils/haptics";
import "../styles/home-surface.css";

function CheckMark() {
  return (
    <span className="hs-commit-check" aria-hidden="true">
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12" />
      </svg>
    </span>
  );
}

/**
 * Implementation intention — "I will do X at Y."
 *
 * Specificity is the active ingredient: committing to a concrete time and a
 * concrete action substantially outperforms a general intention to exercise.
 * The default suggestion comes from today's mood so the ask is always sized to
 * what the person can actually do right now — and on a drained day, a ten
 * minute walk is presented as the whole goal, not as a smaller consolation.
 */
export default function CommitmentCard({ userId, date, mood, energy, onStart, onDone }) {
  const [commitment, setCommitmentState] = useState(() => getCommitment(userId, date));
  const [editing, setEditing] = useState(false);
  const [resting, setResting] = useState(false);
  const suggestion = mood != null && energy != null ? prescriptionFor(mood, energy) : null;

  const [time, setTime] = useState(() => {
    const d = new Date();
    d.setHours(d.getHours() + 1, 0, 0, 0);
    return `${String(d.getHours()).padStart(2, "0")}:00`;
  });
  const [what, setWhat] = useState(suggestion?.label || "");

  const status = commitmentStatus(commitment, new Date());

  const save = () => {
    if (!what.trim()) return;
    hapticLight();
    setCommitmentState(setCommitment(userId, { date, time, what }));
    setEditing(false);
  };

  /**
   * "I did it" is the entry to the close — the after-reading is the half of
   * the mechanism that teaches anything, so completing without being asked how
   * it went throws away the only output this product has. The parent owns that
   * flow (it is full-screen), and marks the commitment done once the reading is
   * in. Without an onDone handler this falls back to a plain completion so the
   * card is never left with a dead button.
   */
  const markDone = () => {
    if (onDone) {
      hapticLight();
      onDone(commitment);
      return;
    }
    hapticSuccess();
    setCommitmentState(completeCommitment(userId, date));
  };

  // ── Chose to rest. A real, unpunished way out of the ask. ──
  if (resting && (!commitment || editing)) {
    return (
      <section className="card card--quiet" aria-label="Today's plan">
        <p className="ds-body">Resting today. That still counts.</p>
        <button type="button" className="ds-link" onClick={() => setResting(false)}>
          Actually, let&apos;s plan something
        </button>
      </section>
    );
  }

  // ── No commitment yet ──
  if (!commitment || editing) {
    return (
      <section className="card ds-stack" aria-label="Today's plan">
        <div>
          {/* No eyebrow — the copy starts cold. Only the two hero cards on this
              screen carry a section label. */}
          <p className="ds-lead">
            {suggestion ? "When will you do it?" : "What will you do today?"}
          </p>
          <p className="ds-support" style={{ marginTop: "var(--space-2)" }}>
            {suggestion
              ? "Naming a time makes it far likelier to happen. Change the plan if something else suits you better."
              : "Naming a time and a thing — now, while you're thinking about it — makes it far likelier to happen."}
          </p>
        </div>

        <div className="hs-commit-time-row">
          <input
            type="time"
            className="hs-commit-time"
            value={time}
            onChange={(e) => setTime(e.target.value)}
            aria-label="What time"
          />
          <input
            className="hs-commit-what"
            value={what}
            onChange={(e) => setWhat(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && save()}
            placeholder={suggestion?.label || "e.g. walk around the block"}
            aria-label="What you'll do"
          />
        </div>

        <div className="hs-commit-actions">
          <button className="btn btn-primary" onClick={save} disabled={!what.trim()}>
            That&apos;s my plan
          </button>
          <button className="btn btn-quiet" onClick={() => setResting(true)}>
            Not today
          </button>
        </div>
      </section>
    );
  }

  // ── Committed ──
  // ── Recorded as not done. A fact about the day, with no word of repair. ──
  if (status === "skipped") {
    return (
      <section className="card card--quiet" aria-label="Today's plan">
        <p className="ds-body">Didn&apos;t happen today. That&apos;s recorded, and that&apos;s all it is.</p>
        <p className="ds-meta" style={{ marginTop: "var(--space-2)" }}>
          Knowing which days don&apos;t work is worth as much as knowing which do.
        </p>
      </section>
    );
  }

  const done = status === "done";
  const late = status === "overdue";
  const now = status === "due";

  if (done) {
    return (
      <section className="card card--quiet" aria-label="Today's plan">
        <div className="hs-commit-done">
          <CheckMark />
          <div>
            <p className="ds-body">{commitment.what}</p>
            <p className="ds-meta">Done. That&apos;s today handled.</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      className={`card ds-stack${now ? " hs-commit--now" : ""}`}
      aria-label="Today's plan"
    >
      <div className="ds-row-between">
        <p className="hs-key hs-key--flush">
          {now ? "It's time" : late ? "Still counts" : "Later today"}
        </p>
        <button
          type="button"
          className="ds-link"
          onClick={() => {
            setWhat(commitment.what);
            setTime(commitment.time);
            setEditing(true);
          }}
        >
          Change
        </button>
      </div>

      <div>
        <p className="ds-meta hs-commit-when">{formatTime(commitment.time)}</p>
        <p className="ds-lead hs-commit-plan">{commitment.what}</p>
      </div>

      {late && (
        <p className="ds-support">
          The time has passed — that&apos;s okay. Later is worth every bit as much.
        </p>
      )}

      <div className="hs-commit-actions">
        {/* Starting takes a reading first. It is the difference between a
            comparison the clinician can defend in session and one credited
            with the whole day since breakfast. One tap, and skippable. */}
        {onStart && (
          <button className="btn btn-primary" onClick={() => onStart(commitment)}>
            Starting now
          </button>
        )}
        <button
          className={onStart ? "btn btn-secondary" : "btn btn-primary"}
          onClick={markDone}
        >
          I did it
        </button>
      </div>

      {/* The only other true thing that can happen to a plan. Without it the
          record contains successes and silences and cannot tell them apart. */}
      <button
        type="button"
        className="ds-link"
        style={{ display: "block", margin: "var(--space-2) auto 0" }}
        onClick={() => {
          hapticLight();
          setCommitmentState(skipCommitment(userId, date));
        }}
      >
        It didn&apos;t happen today
      </button>
    </section>
  );
}
